import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonLabel,
  IonModal,
  IonPage,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonRouter,
} from '@ionic/react';
import {
  autoAssignTrittico,
  bedLineDietCode,
  bedLineIsScorporata,
  hasMealTemplate,
  splitBedsForReview,
  type CatalogRowLite,
  type SupplyRequestLine,
  type TritticoAssignment,
  type TritticoSlot,
} from '@trackeat/shared';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { getSupabase } from '../../lib/supabase.js';

// T-436 — RIEPILOGO E CONTROLLO del preposto. È il punto dove il preposto VEDE
// tutti gli ordini per letto dei reparti (consolidati), li controlla e li invia
// al centro cottura per la produzione. L'input per-letto sta in PrepostoBedsPage;
// qui si CONSOLIDA e si SCHEDULA (invio). La cucina vede l'ordine solo dopo l'invio.

type MealService = 'pranzo' | 'cena' | 'colazione';
const MEAL_LABELS: Record<MealService, string> = { pranzo: 'Pranzo', cena: 'Cena', colazione: 'Colazione' };
const todayIso = (): string => new Date().toISOString().slice(0, 10);
const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const lettoNum = (s: string): number => {
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : 9999;
};

interface OrderRow {
  id: string;
  status: string;
  lines: SupplyRequestLine[] | null;
  submitted_at: string | null;
  created_at: string;
}
interface CatalogRow { id: string; category: string; label: string; sort_order: number }

// T-441/T-445 — composizione locale (working state) in editing prima del salvataggio.
// T-445: da 3 a 5 slot → primo/secondo/contorno + FRUTTA (catalogo, categoria FRUTTA)
// + BEVANDA (5° slot, lista fissa — il catalogo non ha una categoria bevande).
type Slot = { label: string; source: 'auto' | 'manual' } | null;
interface Trittico { primo: Slot; secondo: Slot; contorno: Slot; frutta: Slot; bevanda: Slot }
type SlotKey = 'primo' | 'secondo' | 'contorno' | 'frutta' | 'bevanda';
// La FRUTTA pesca dalla categoria FRUTTA del catalogo (come gli altri slot-piatto);
// la BEVANDA è gestita a parte nel picker (lista fissa) → non ha una sezione catalogo.
const SLOT_SECTION: Record<Exclude<SlotKey, 'bevanda'>, string> = { primo: 'PRIMI', secondo: 'SECONDI', contorno: 'CONTORNI', frutta: 'FRUTTA' };
const SLOT_LABEL: Record<SlotKey, string> = { primo: 'Primo', secondo: 'Secondo', contorno: 'Contorno', frutta: 'Frutta', bevanda: 'Bevanda' };
const EMPTY_TRIT: Trittico = { primo: null, secondo: null, contorno: null, frutta: null, bevanda: null };
const SLOT_ORDER: SlotKey[] = ['primo', 'secondo', 'contorno', 'frutta', 'bevanda'];
// T-445 — BEVANDA: lista fissa in codice (nessuna categoria bevande a catalogo).
// "Acqua" è il default; "-" = nessuna bevanda (digiuno/parenterale).
const BEVANDA_DEFAULT = 'Acqua';
const BEVANDA_OPTIONS: string[] = [BEVANDA_DEFAULT, 'Tè', 'Succo di frutta', '-'];
const slotFromLine = (s: TritticoSlot | null | undefined): Slot => (s ? { label: s.label, source: s.source ?? 'auto' } : null);
const slotToStore = (s: Slot): TritticoSlot | null => (s ? { label: s.label, source: s.source } : null);
// Server ⊕ auto-pop: lo slot del server (scelta salvata) vince; se vuoto, riempie
// con l'auto-popolamento (source 'auto', ri-sovrascrivibile alla prossima passata).
// La BEVANDA di default è "Acqua" se né il server né il template la fissano.
const mergeTrit = (server: Trittico, auto: TritticoAssignment): Trittico => ({
  primo: server.primo ?? (auto.primo ? { label: auto.primo, source: 'auto' } : null),
  secondo: server.secondo ?? (auto.secondo ? { label: auto.secondo, source: 'auto' } : null),
  contorno: server.contorno ?? (auto.contorno ? { label: auto.contorno, source: 'auto' } : null),
  frutta: server.frutta ?? (auto.frutta ? { label: auto.frutta, source: 'auto' } : null),
  bevanda: server.bevanda ?? (auto.bevanda ? { label: auto.bevanda, source: 'auto' } : { label: BEVANDA_DEFAULT, source: 'auto' }),
});

export default function PrepostoReviewPage(): JSX.Element {
  const { profile, signOut } = useAuth();
  const sb = useMemo(() => getSupabase(), []);
  const router = useIonRouter();
  const preposto = profile?.preposto ?? null;

  const [loading, setLoading] = useState(true);
  const [destName, setDestName] = useState('');
  const [serviceDate, setServiceDate] = useState<string>(todayIso());
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false); // popup conferma prima dell'invio
  const [toast, setToast] = useState<string | null>(null);
  // T-443 — pranzo/cena sono ordini SEPARATI: il preposto rivede e invia UN servizio
  // alla volta. Questo è il servizio attualmente selezionato nel riepilogo.
  const [reviewMeal, setReviewMeal] = useState<MealService>('pranzo');
  // T-441 — trittico piatti
  const [catalog, setCatalog] = useState<CatalogRow[]>([]);
  const [tritById, setTritById] = useState<Record<string, Trittico>>({});
  const [picker, setPicker] = useState<{ key: string; slot: SlotKey; title: string } | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [savingTrit, setSavingTrit] = useState(false);
  // T-436 fix — stampa IN-APP (iframe in un modale), non window.open (che su app
  // nativa apriva una webview bianca che si incastrava, senza indietro né stampa).
  const [printHtml, setPrintHtml] = useState<string | null>(null);
  const printRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!preposto) return;
    let mounted = true;
    void (async () => {
      const { data } = await sb.from('destinations').select('name').eq('id', preposto.destination_id).maybeSingle();
      if (mounted) setDestName((data as { name?: string } | null)?.name ?? '');
    })();
    return () => {
      mounted = false;
    };
  }, [sb, preposto]);

  const load = useCallback(async (): Promise<void> => {
    if (!preposto) return;
    setLoading(true);
    const { data, error } = await sb
      .from('supply_requests')
      .select('id,status,lines,submitted_at,created_at')
      .eq('destination_id', preposto.destination_id)
      .eq('requested_for_date', serviceDate)
      .is('archived_at', null)
      .in('status', ['draft', 'submitted', 'accepted', 'in_production', 'fulfilled'])
      .order('created_at', { ascending: false });
    if (error) {
      setToast(`Errore caricamento: ${error.message}`);
      setLoading(false);
      return;
    }
    setOrders((data ?? []) as OrderRow[]);
    setLoading(false);
  }, [sb, preposto, serviceDate]);

  useEffect(() => {
    void load();
  }, [load]);

  // Righe per-LETTO degli ordini ANCORA MODIFICABILI (draft/submitted). Review bug
  // fix (T-441): includere accepted/in_production/fulfilled rendeva assegnabile un
  // trittico che `saveTrittico` non può salvare (scrive solo draft/submitted) →
  // mostrato/stampato ma non persistito. Assignable-set == writable-set.
  const bedLines = useMemo<SupplyRequestLine[]>(
    () =>
      orders
        .filter((o) => o.status === 'draft' || o.status === 'submitted')
        .flatMap((o) => (o.lines ?? []).filter((l) => (l.letto ?? '').trim())),
    [orders],
  );
  // Ripartizione per reparto (nome ward → n letti) del servizio SELEZIONATO.
  const byWard = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of bedLines) {
      if ((l.meal_service ?? 'pranzo') !== reviewMeal) continue;
      const w = (l.reparto ?? '').trim() || '(senza reparto)';
      m.set(w, (m.get(w) ?? 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [bedLines, reviewMeal]);

  // T-441 — catalogo piatti (online-diretto; RLS già consente al preposto).
  useEffect(() => {
    let mounted = true;
    void (async () => {
      const { data } = await sb.from('order_catalog_rows').select('id,category,label,sort_order').eq('active', true).order('sort_order');
      if (mounted) setCatalog((data as CatalogRow[] | null) ?? []);
    })();
    return () => { mounted = false; };
  }, [sb]);

  // T-443 — servizio di una riga-ordine (invariante single-meal).
  const rowMealOf = useCallback((o: OrderRow): MealService => {
    for (const ln of o.lines ?? []) return (ln.meal_service ?? 'pranzo') as MealService;
    return 'pranzo';
  }, []);

  // T-441/T-443 — split di TUTTI i servizi (per inizializzare il trittico su ognuno) +
  // viste filtrate sul servizio SELEZIONATO (ciò che si rende/invia).
  const splitAll = useMemo(() => splitBedsForReview(bedLines), [bedLines]);
  const dietGroups = useMemo(() => splitAll.dietGroups.filter((g) => g.meal_service === reviewMeal), [splitAll, reviewMeal]);
  const scorporate = useMemo(() => splitAll.scorporate.filter((s) => s.meal_service === reviewMeal), [splitAll, reviewMeal]);
  const catalogLite = useMemo<CatalogRowLite[]>(() => catalog.map((c) => ({ label: c.label, category: c.category, sort_order: c.sort_order })), [catalog]);

  // T-441 — inizializza il trittico working: valore server ⊕ auto-pop. Ri-parte
  // solo quando cambiano i letti/il catalogo (reload/date/servizio) → durante
  // l'editing le modifiche locali restano. Non scrive nulla (auto-pop client-only).
  useEffect(() => {
    const server = (pred: (l: SupplyRequestLine) => boolean): Trittico => {
      const l = bedLines.find(pred);
      return { primo: slotFromLine(l?.primo), secondo: slotFromLine(l?.secondo), contorno: slotFromLine(l?.contorno), frutta: slotFromLine(l?.frutta), bevanda: slotFromLine(l?.bevanda) };
    };
    const next: Record<string, Trittico> = {};
    for (const g of splitAll.dietGroups) {
      const srv = server((l) => (l.letto ?? '').trim() !== '' && !bedLineIsScorporata(l) && (l.meal_service ?? 'pranzo') === g.meal_service && bedLineDietCode(l) === g.dietCode);
      next[g.key] = mergeTrit(srv, autoAssignTrittico(g.dietCode, catalogLite));
    }
    for (const s of splitAll.scorporate) {
      const srv = server((l) => bedLineIsScorporata(l) && (l.meal_service ?? 'pranzo') === s.meal_service && (l.reparto ?? '') === (s.reparto ?? '') && (l.letto ?? '').trim() === s.letto);
      next[s.key] = mergeTrit(srv, autoAssignTrittico(s.dietCode, catalogLite));
    }
    setTritById(next);
  }, [splitAll, catalogLite, bedLines]);

  const setSlot = useCallback((key: string, slot: SlotKey, label: string) => {
    setTritById((m) => ({ ...m, [key]: { ...(m[key] ?? EMPTY_TRIT), [slot]: { label, source: 'manual' as const } } }));
  }, []);

  const draftOrders = useMemo(() => orders.filter((o) => o.status === 'draft'), [orders]);
  const submittedOrders = useMemo(() => orders.filter((o) => o.status === 'submitted'), [orders]);

  // ── Viste PER SERVIZIO (pranzo/cena separati). Il preposto rivede/invia un servizio. ──
  const bedLinesForMeal = useMemo(() => bedLines.filter((l) => (l.meal_service ?? 'pranzo') === reviewMeal), [bedLines, reviewMeal]);
  const totalMealsForMeal = bedLinesForMeal.length;
  const draftOrdersForMeal = useMemo(() => draftOrders.filter((o) => rowMealOf(o) === reviewMeal), [draftOrders, reviewMeal, rowMealOf]);
  const draftMealsForMeal = useMemo(() => draftOrdersForMeal.flatMap((o) => (o.lines ?? []).filter((l) => (l.letto ?? '').trim())).length, [draftOrdersForMeal]);
  const sentMealsForMeal = totalMealsForMeal - draftMealsForMeal;
  const submittedForMeal = useMemo(() => submittedOrders.filter((o) => rowMealOf(o) === reviewMeal), [submittedOrders, reviewMeal, rowMealOf]);

  // Ordini già ACCETTATI dalla cucina: non più modificabili, ma consultabili (sola lettura).
  const acceptedForMeal = useMemo(
    () => orders.filter((o) => ['accepted', 'in_production', 'fulfilled'].includes(o.status) && rowMealOf(o) === reviewMeal),
    [orders, reviewMeal, rowMealOf],
  );
  const acceptedBedsForMeal = useMemo(() => acceptedForMeal.flatMap((o) => (o.lines ?? []).filter((l) => (l.letto ?? '').trim())), [acceptedForMeal]);
  const acceptedStatus = acceptedForMeal[0]?.status ?? null;

  // Servizi con letti (in qualunque stato non-rifiutato) → per il selettore + gate "vuoto".
  const servicesWithBeds = useMemo(() => {
    const s = new Set<MealService>();
    for (const o of orders) {
      if (o.status === 'rejected') continue;
      for (const l of o.lines ?? []) if ((l.letto ?? '').trim()) s.add((l.meal_service ?? 'pranzo') as MealService);
    }
    return s;
  }, [orders]);
  const anyBeds = servicesWithBeds.size > 0;
  // Conteggio letti per servizio (ogni stato non-rifiutato) → etichette del selettore.
  const bedsCountByService = useMemo(() => {
    const m = new Map<MealService, number>();
    for (const o of orders) {
      if (o.status === 'rejected') continue;
      for (const l of o.lines ?? []) if ((l.letto ?? '').trim()) { const s = (l.meal_service ?? 'pranzo') as MealService; m.set(s, (m.get(s) ?? 0) + 1); }
    }
    return m;
  }, [orders]);

  // Se il servizio selezionato non ha letti ma un altro sì, seleziona il primo utile.
  useEffect(() => {
    if (servicesWithBeds.size === 0 || servicesWithBeds.has(reviewMeal)) return;
    const first = (['pranzo', 'cena', 'colazione'] as MealService[]).find((m) => servicesWithBeds.has(m));
    if (first) setReviewMeal(first);
  }, [servicesWithBeds, reviewMeal]);

  // T-441 — applica il trittico (working state) alle righe: fan-out per gruppo
  // dieta (tutte le bed-line stesso meal+dietCode, non scorporate) + scorporata
  // (solo la sua riga, per identità). Non tocca righe catalogo/altri gruppi.
  const applyTritToLines = useCallback((lines: SupplyRequestLine[]): SupplyRequestLine[] =>
    lines.map((l) => {
      if ((l.letto ?? '').trim() === '') return l;
      const meal = l.meal_service ?? 'pranzo';
      if (bedLineIsScorporata(l)) {
        const t = tritById[`${meal}|${l.reparto ?? ''}|${(l.letto ?? '').trim()}|${bedLineDietCode(l)}`];
        return t ? { ...l, primo: slotToStore(t.primo), secondo: slotToStore(t.secondo), contorno: slotToStore(t.contorno), frutta: slotToStore(t.frutta), bevanda: slotToStore(t.bevanda), trittico_locked: true } : l;
      }
      const t = tritById[`${meal}::${bedLineDietCode(l)}`];
      return t ? { ...l, primo: slotToStore(t.primo), secondo: slotToStore(t.secondo), contorno: slotToStore(t.contorno), frutta: slotToStore(t.frutta), bevanda: slotToStore(t.bevanda) } : l;
    }), [tritById]);

  // T-441 — persiste il trittico su UN ordine: RE-READ fresh (evita di sovrascrivere
  // righe aggiunte da un QR/beds concorrente con uno snapshot stantìo) + OCC su
  // `version` (bump+check). `promote`=true invia anche (status draft→submitted)
  // atomicamente. Ritorna esito per gestire conflitto (reload).
  const persistTritToOrder = useCallback(
    async (orderId: string, promote: boolean): Promise<'ok' | 'nochange' | 'conflict' | 'error'> => {
      const { data: fresh, error: selErr } = await sb.from('supply_requests').select('lines,version,status').eq('id', orderId).maybeSingle();
      if (selErr) return 'error';
      const f = fresh as { lines: SupplyRequestLine[] | null; version: number | null; status: string } | null;
      if (!f) return 'conflict';
      if (f.status !== 'draft' && f.status !== 'submitted') return 'conflict';
      if (promote && f.status !== 'draft') return 'conflict';
      const oldLines = f.lines ?? [];
      const newLines = applyTritToLines(oldLines);
      const changed = JSON.stringify(newLines) !== JSON.stringify(oldLines);
      if (!promote && !changed) return 'nochange';
      const patch: Record<string, unknown> = { lines: newLines, version: (f.version ?? 0) + 1 };
      if (promote) { patch.status = 'submitted'; patch.submitted_at = new Date().toISOString(); patch.submitted_by = profile?.id ?? null; }
      let q = sb.from('supply_requests').update(patch).eq('id', orderId).eq('version', f.version ?? 0);
      if (promote) q = q.eq('status', 'draft');
      const { data: upd, error } = await q.select('id');
      if (error) return 'error';
      if (!upd || upd.length === 0) return 'conflict';
      return 'ok';
    },
    [sb, applyTritToLines, profile],
  );

  // Invia al centro cottura: applica il trittico E promuove draft→submitted in una
  // sola scrittura per ordine (bug review: prima l'invio non salvava il trittico).
  const sendToKitchen = useCallback(async () => {
    if (!preposto || draftOrdersForMeal.length === 0) return; // invia SOLO il servizio selezionato
    // T-445 — BLOCCO INVIO: una dieta SENZA composizione automatica (slot arancioni)
    // NON deve arrivare in produzione come "sola dieta" (finirebbe come un piatto
    // unico da matchare). Il preposto deve prima comporne i piatti. Le diete senza
    // pasto per natura (idrica/digiuno/parenterale) sono esenti (nessun piatto da comporre).
    const MEAL_LESS = new Set(['idrica', 'digiuno', 'parenterale']);
    const notComposed = (key: string, dietCode: string): boolean => {
      if (hasMealTemplate(dietCode) || MEAL_LESS.has(dietCode)) return false;
      const t = tritById[key];
      return !(t?.primo?.label || t?.secondo?.label || t?.contorno?.label || t?.frutta?.label);
    };
    const incomplete = [
      ...dietGroups.filter((g) => notComposed(g.key, g.dietCode)).map((g) => g.dietLabel),
      ...scorporate.filter((s) => notComposed(s.key, s.dietCode)).map((s) => `${s.dietLabel} (letto ${s.letto})`),
    ];
    if (incomplete.length > 0) {
      setToast(`⚠️ Componi i piatti (slot arancioni) prima di inviare: ${[...new Set(incomplete)].join(' · ')}`);
      return;
    }
    setSending(true);
    try {
      for (const o of draftOrdersForMeal) {
        const r = await persistTritToOrder(o.id, true);
        if (r === 'error') { setToast('Errore invio — riprova'); await load(); return; }
        if (r === 'conflict') { setToast('Un ordine è cambiato nel frattempo — ricarico'); await load(); return; }
      }
      setToast(`${MEAL_LABELS[reviewMeal]} verificato e inviato al centro cottura ✓`);
      await load();
    } finally {
      setSending(false);
    }
  }, [preposto, draftOrdersForMeal, reviewMeal, persistTritToOrder, load, dietGroups, scorporate, tritById]);

  // T-436 — stampa un foglio A4 riepilogo: tutti i letti (per reparto) con dieta
  // e note + la sintesi per dieta. Apre una finestra stampabile (PWA/desktop).
  const printSummary = useCallback((): void => {
    const dateLabel = new Date(serviceDate).toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
    // T-443 — il foglio deve mostrare TUTTO l'ordine del servizio: sia i letti ancora
    // in controllo/inviati (bedLinesForMeal) sia quelli GIÀ ACCETTATI dalla cucina
    // (acceptedBedsForMeal), altrimenti per un servizio accettato stampa "0 pasti".
    const printLines = [...bedLinesForMeal, ...acceptedBedsForMeal];
    const printTotal = printLines.length;
    // Righe per reparto, ordinate per numero letto.
    const wardsMap = new Map<string, SupplyRequestLine[]>();
    for (const l of printLines) {
      const w = (l.reparto ?? '').trim() || 'Senza reparto';
      (wardsMap.get(w) ?? wardsMap.set(w, []).get(w)!).push(l);
    }
    // Trittico salvato sulle righe accettate (non è nel working state `tritById`).
    const acceptedTrit: Record<string, { primo?: string; secondo?: string; contorno?: string; frutta?: string; bevanda?: string }> = {};
    for (const l of acceptedBedsForMeal) {
      const meal = (l.meal_service ?? 'pranzo') as MealService;
      const dc = bedLineDietCode(l);
      const key = bedLineIsScorporata(l) ? `${meal}|${l.reparto ?? ''}|${(l.letto ?? '').trim()}|${dc}` : `${meal}::${dc}`;
      if (!acceptedTrit[key]) acceptedTrit[key] = { primo: l.primo?.label, secondo: l.secondo?.label, contorno: l.contorno?.label, frutta: l.frutta?.label, bevanda: l.bevanda?.label };
    }
    const printSplit = splitBedsForReview(printLines);
    const wardBlocks = Array.from(wardsMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([w, ls]) => {
        const rows = ls
          .slice()
          .sort((a, b) => lettoNum(a.letto ?? '') - lettoNum(b.letto ?? '') || (a.letto ?? '').localeCompare(b.letto ?? ''))
          .map((l) => `<tr><td class="c">${escapeHtml(l.letto ?? '')}</td><td>${escapeHtml(l.description ?? '')}</td><td class="note">${escapeHtml(l.notes ?? '')}</td></tr>`)
          .join('');
        return `<h3>${escapeHtml(w)} <span class="wn">${ls.length} ${ls.length === 1 ? 'letto' : 'letti'}</span></h3>
          <table><thead><tr><th class="c">Letto</th><th>Dieta</th><th>Note</th></tr></thead><tbody>${rows}</tbody></table>`;
      })
      .join('');
    // T-441 — ordine finito col trittico assegnato (working state corrente).
    // T-443 — il foglio è per il SERVIZIO selezionato → nessuna colonna servizio.
    const svcCol = false;
    // Trittico: working state (draft/submitted) oppure valore salvato (accettati).
    const cell = (key: string, sk: SlotKey): string => escapeHtml(tritById[key]?.[sk]?.label ?? acceptedTrit[key]?.[sk] ?? '—');
    const blockARows = printSplit.dietGroups
      .map((g) => `<tr><td>${escapeHtml(g.dietLabel)}</td>${svcCol ? `<td class="c">${escapeHtml(MEAL_LABELS[g.meal_service])}</td>` : ''}<td class="c">${g.meals}</td><td>${cell(g.key, 'primo')}</td><td>${cell(g.key, 'secondo')}</td><td>${cell(g.key, 'contorno')}</td><td>${cell(g.key, 'frutta')}</td><td>${cell(g.key, 'bevanda')}</td></tr>`)
      .join('');
    const blockBRows = printSplit.scorporate
      .map((s) => `<tr><td>${escapeHtml(s.reparto ?? '')}</td><td class="c">${escapeHtml(s.letto)}</td><td>${escapeHtml(s.dietLabel)}</td><td class="note">${escapeHtml(s.notes ?? '')}</td><td>${cell(s.key, 'primo')}</td><td>${cell(s.key, 'secondo')}</td><td>${cell(s.key, 'contorno')}</td><td>${cell(s.key, 'frutta')}</td><td>${cell(s.key, 'bevanda')}</td></tr>`)
      .join('');
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Ordine ${escapeHtml(destName)} ${escapeHtml(serviceDate)}</title>
      <style>
        *{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1a2b2e;margin:0;padding:24px 26px}
        .hd{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #0b6b63;padding-bottom:8px;margin-bottom:14px}
        .hd h1{margin:0;font-size:22px;color:#0b6b63} .hd .d{font-size:14px;color:#475569;text-transform:capitalize}
        .tot{font-size:15px;font-weight:700;margin:0 0 14px}
        h3{font-size:14px;margin:16px 0 6px;color:#0b6b63;border-bottom:1px solid #cfe0dd;padding-bottom:3px} h3 .wn{font-weight:400;color:#64748b;font-size:12px}
        table{width:100%;border-collapse:collapse;margin:0 0 6px;font-size:13px} th,td{border:1px solid #cbd5e1;padding:5px 8px;text-align:left} th{background:#eef4f4;font-size:11px;text-transform:uppercase;letter-spacing:.03em}
        td.c,th.c{text-align:center;width:56px;font-weight:700} td.note{color:#475569;font-size:12px}
        .diets{margin-top:18px} .diets table{max-width:340px} .diets.full table{max-width:none}
        @media print{body{padding:10px} .noprint{display:none}}
        .noprint{margin-top:20px;text-align:center} .noprint button{font-size:15px;padding:10px 22px;border:none;border-radius:8px;background:#0b6b63;color:#fff;cursor:pointer}
      </style></head><body>
      <div class="hd"><div><h1>${escapeHtml(destName || 'Ordine pasti')} — ${escapeHtml(MEAL_LABELS[reviewMeal])}</h1><div class="d">${escapeHtml(dateLabel)}</div></div><div class="d">${printTotal} pasti</div></div>
      <div class="tot">Totale ${escapeHtml(MEAL_LABELS[reviewMeal].toLowerCase())}: ${printTotal} pasti${draftMealsForMeal > 0 ? ` · ⚠️ ${draftMealsForMeal} ancora in controllo (non inviati)` : ''}</div>
      ${wardBlocks}
      <div class="diets full"><h3>Ordine finito — trittico per dieta</h3>
        <table><thead><tr><th>Dieta</th>${svcCol ? '<th class="c">Servizio</th>' : ''}<th class="c">N°</th><th>Primo</th><th>Secondo</th><th>Contorno</th><th>Frutta</th><th>Bevanda</th></tr></thead><tbody>${blockARows}</tbody></table></div>
      ${blockBRows ? `<div class="diets full"><h3>Diete personalizzate (con note)</h3>
        <table><thead><tr><th>Reparto</th><th class="c">Letto</th><th>Dieta</th><th>Nota</th><th>Primo</th><th>Secondo</th><th>Contorno</th><th>Frutta</th><th>Bevanda</th></tr></thead><tbody>${blockBRows}</tbody></table></div>` : ''}
      </body></html>`;
    // Mostra il foglio in un modale IN-APP (iframe) → sempre chiudibile, stampabile.
    setPrintHtml(html);
  }, [serviceDate, bedLinesForMeal, acceptedBedsForMeal, destName, draftMealsForMeal, reviewMeal, tritById]);

  // Stampa il contenuto dell'iframe (browser print dialog su web/PWA; framework di
  // stampa nativo su Android/iOS WebView). Se non disponibile, il foglio resta
  // comunque visibile e chiudibile.
  const doPrint = useCallback((): void => {
    try {
      const win = printRef.current?.contentWindow;
      win?.focus();
      win?.print();
    } catch { setToast('Stampa non disponibile su questo dispositivo — usa uno screenshot'); }
  }, []);

  // T-441 — salva il trittico (senza inviare) su tutti gli ordini modificabili del
  // giorno, via persistTritToOrder (re-read fresh + OCC version). Una PATCH per
  // ordine solo se cambia qualcosa.
  const saveTrittico = useCallback(async () => {
    if (!preposto) return;
    setSavingTrit(true);
    try {
      // Salva il trittico SOLO del servizio selezionato (non scrivere valori auto-pop
      // su servizi che il preposto non ha aperto).
      const editable = [...draftOrdersForMeal, ...submittedForMeal];
      let changed = 0;
      for (const o of editable) {
        const r = await persistTritToOrder(o.id, false);
        if (r === 'error') { setToast('Errore salvataggio — riprova'); await load(); return; }
        if (r === 'conflict') { setToast('Ordine aggiornato altrove — ricarico'); await load(); return; }
        if (r === 'ok') changed += 1;
      }
      setToast(changed > 0 ? 'Trittico salvato ✓' : 'Nessuna modifica da salvare');
      await load();
    } finally {
      setSavingTrit(false);
    }
  }, [preposto, draftOrdersForMeal, submittedForMeal, persistTritToOrder, load]);

  // Slot della composizione (5 bottoni: primo/secondo/contorno/frutta/bevanda) per
  // una riga (gruppo o scorporata). `dietCode` serve per l'evidenza ARANCIONE (T-445):
  // le diete SENZA template auto (hasMealTemplate=false) hanno gli slot-piatto da
  // comporre a mano → sfondo arancione finché non scelti. La bevanda (default "Acqua")
  // non va MAI arancione.
  const renderSlots = (key: string, dietCode: string): JSX.Element => {
    const t = tritById[key] ?? EMPTY_TRIT;
    const noTemplate = !hasMealTemplate(dietCode);
    return (
      <div className="rv-slots">
        {SLOT_ORDER.map((sk) => {
          const slot = t[sk];
          const isManual = slot?.source === 'manual';
          // "Da comporre a mano": dieta senza template + slot-piatto non scelto
          // (nessuna label, non manuale). Mai sulla bevanda (ha sempre "Acqua").
          const isTodo = sk !== 'bevanda' && noTemplate && !isManual && !slot?.label;
          const cls = `rv-slot${isTodo ? ' todo' : isManual ? ' man' : !slot ? ' empty' : ''}`;
          return (
            <button
              key={sk}
              type="button"
              className={cls}
              onClick={() => { setPickerSearch(''); setPicker({ key, slot: sk, title: SLOT_LABEL[sk] }); }}
            >
              <span className="k">{SLOT_LABEL[sk]}</span>
              <span className="v">{slot?.label ?? '— scegli —'}</span>
            </button>
          );
        })}
      </div>
    );
  };

  if (!preposto) {
    return (
      <IonPage>
        <IonContent className="ion-padding">Accesso non valido.</IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <style>{`
        .rv-hero{background:linear-gradient(135deg,#0b6b63,#12a594);color:#fff;border-radius:16px;padding:16px 18px;margin:4px 0 14px;box-shadow:0 4px 14px rgba(0,0,0,.12)}
        .rv-hero h2{margin:0 0 2px;font-size:20px;font-weight:800}
        .rv-hero .sub{opacity:.85;font-size:13px}
        .rv-date input{border:1px solid var(--ion-color-step-300,#cfd8dc);border-radius:10px;padding:8px 10px;font-size:14px;background:var(--ion-background-color,#fff);color:inherit}
        .rv-status{border-radius:12px;padding:10px 14px;margin:0 0 12px;font-size:13px;line-height:1.4}
        .rv-status.draft{background:#eef4ff;border:1px solid #c3d6f5;color:#274a8a}
        .rv-status.sent{background:#e8f5ec;border:1px solid #bfe3cb;color:#1e6b3a}
        .rv-card{background:var(--ion-card-background,#fff);border:1px solid #e5edef;border-radius:14px;padding:12px 14px;margin:0 0 12px;box-shadow:0 1px 6px rgba(0,0,0,.05)}
        .rv-card h3{margin:0 0 8px;font-size:14px;font-weight:800;color:#0b6b63}
        .rv-chips{display:flex;flex-wrap:wrap;gap:6px}
        .rv-chip{font-size:13px;padding:5px 11px;border-radius:999px;background:#eef4f4;border:1px solid #d6e6e4;color:#245a68;font-weight:600}
        .rv-row{display:flex;align-items:baseline;gap:8px;padding:7px 0;border-top:1px solid #eef2f3}
        .rv-row:first-of-type{border-top:none}
        .rv-diet{flex:1;font-size:14px;font-weight:700;color:#243b40}
        .rv-n{font-size:16px;font-weight:800;color:#12a594;min-width:30px;text-align:right}
        .rv-beds{font-size:12px;color:#64748b;margin:2px 0 0}
        .rv-empty{text-align:center;color:#64748b;padding:26px 12px;font-size:14px;line-height:1.5}
        .rv-big{font-size:26px;font-weight:800;color:#0b6b63}
        .rv-hint{font-size:12px;color:#64748b;margin:0 0 10px;line-height:1.4}
        .rv-trit{border:1px solid #e5edef;border-radius:12px;padding:10px;margin:0 0 10px;background:#fbfdfd}
        .rv-trit.scorp{border-color:#f0d9a8;background:#fffdf6}
        .rv-trit-h{display:flex;align-items:center;gap:8px;margin-bottom:8px;flex-wrap:wrap}
        .rv-trit-h .rv-diet{flex:1;font-size:15px;font-weight:800;color:#243b40}
        .rv-trit-h .rv-n{font-size:15px;font-weight:800;color:#12a594}
        .rv-svc{font-size:11px;font-weight:700;background:#e6f7f4;color:#0b6b63;border-radius:999px;padding:2px 9px}
        .rv-badge{font-size:11px;font-weight:700;background:#eef4f4;color:#245a68;border-radius:999px;padding:2px 9px}
        .rv-scorp-note{font-size:13px;color:#7a5b13;background:#fff7e6;border:1px solid #f0d9a8;border-radius:8px;padding:6px 9px;margin:0 0 8px}
        .rv-svc-seg{margin:0 2px 12px}
        .rv-accepted{border:1px solid #bfe3cb;background:#f3fbf6}
        .rv-acc{border-top:1px solid #dcefe3;padding:8px 0}
        .rv-acc:first-of-type{border-top:none}
        .rv-acc-h{font-size:14px;color:#1a2b2e}
        .rv-acc-t{font-size:13px;color:#0b6b63;margin-top:3px}
        .rv-slots{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px}
        .rv-slot{min-width:0;display:flex;flex-direction:column;gap:2px;align-items:flex-start;text-align:left;border:1px solid #d3dde0;border-radius:9px;padding:7px 8px;background:#fff;cursor:pointer}
        .rv-slot .k{font-size:10px;font-weight:800;letter-spacing:.03em;text-transform:uppercase;color:#94a3b8}
        .rv-slot .v{font-size:13px;font-weight:600;color:#243b40;line-height:1.15;word-break:break-word}
        .rv-slot.empty{border-color:#e0a94f;background:#fffaf0}
        .rv-slot.empty .v{color:#9a6212}
        /* T-445 — "da comporre a mano": dieta senza template, slot-piatto non scelto */
        .rv-slot.todo{border-color:#f59e0b;background:#ffe8cc}
        .rv-slot.todo .k{color:#b45309}
        .rv-slot.todo .v{color:#9a3412;font-weight:700}
        .rv-slot.man{border-color:#12a594;background:#f0fbf9}
        .rv-pick{display:flex;align-items:center;justify-content:space-between;width:100%;text-align:left;background:var(--ion-card-background,#fff);border:1px solid #e5edef;border-radius:11px;padding:12px 14px;margin:0 0 7px;font-size:15px;font-weight:600;color:#243b40;cursor:pointer}
        .rv-pick.on{border-color:#12a594;background:#e8f7f4;color:#0b6b63}
        .rv-pick.clear{color:#94a3b8;font-weight:500;justify-content:center;font-size:14px}
        .rv-pick .tick{color:#12a594;font-weight:800}
      `}</style>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/preposto" />
          </IonButtons>
          <IonTitle>Riepilogo e invio</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void signOut()}>Esci</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="rv-hero">
          <h2>{destName || 'La mia struttura'}</h2>
          <div className="sub">Tutti gli ordini per letto dei reparti · controlla e invia al centro cottura</div>
        </div>

        <div style={{ margin: '0 2px 12px' }}>
          <input
            type="date"
            className="rv-date"
            value={serviceDate}
            onChange={(e) => setServiceDate(e.target.value || todayIso())}
            aria-label="Data del servizio"
          />
        </div>

        {/* T-443 — selettore SERVIZIO: pranzo/cena sono ordini separati, si rivedono e
            inviano uno alla volta. Mostra solo i servizi con letti (incl. già accettati). */}
        {!loading && anyBeds && (
          <IonSegment className="rv-svc-seg" value={reviewMeal} onIonChange={(e) => setReviewMeal((e.detail.value as MealService) ?? 'pranzo')}>
            {(['colazione', 'pranzo', 'cena'] as MealService[]).filter((s) => servicesWithBeds.has(s)).map((s) => (
              <IonSegmentButton key={s} value={s}>
                <IonLabel>{MEAL_LABELS[s]} ({bedsCountByService.get(s) ?? 0})</IonLabel>
              </IonSegmentButton>
            ))}
          </IonSegment>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <IonSpinner />
          </div>
        ) : !anyBeds ? (
          <div className="rv-empty">
            Nessun ordine per letto per questa data.<br />
            Vai su <strong>«Ordine per letto»</strong> per inserire i letti dei reparti.
            <div style={{ marginTop: 14 }}>
              <IonButton fill="outline" onClick={() => router.push('/preposto/ordini-letto', 'forward')}>
                🛏️ Vai a Ordine per letto
              </IonButton>
            </div>
          </div>
        ) : (
          <>
            {draftMealsForMeal > 0 && (
              <div className="rv-status draft">
                📝 <strong>{draftMealsForMeal} {draftMealsForMeal === 1 ? 'letto' : 'letti'} in controllo</strong> per {MEAL_LABELS[reviewMeal].toLowerCase()} — non ancora inviati al
                centro cottura. Controlla e premi «Verifica e invia» in fondo.
              </div>
            )}
            {sentMealsForMeal > 0 && (
              <div className="rv-status sent">
                📤 <strong>{sentMealsForMeal} {sentMealsForMeal === 1 ? 'letto' : 'letti'} già inviati</strong> ({MEAL_LABELS[reviewMeal].toLowerCase()})
                {submittedForMeal[0]?.submitted_at ? ` il ${new Date(submittedForMeal[0].submitted_at!).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}.
              </div>
            )}

            {/* Totale del servizio selezionato + ripartizione reparto (solo se ci sono
                letti da controllare/inviati; per un servizio interamente accettato la
                sintesi sta nella card "accettato" sotto). */}
            {totalMealsForMeal > 0 && (
              <>
                <div className="rv-card">
                  <h3>{MEAL_LABELS[reviewMeal]}</h3>
                  <div className="rv-big">{totalMealsForMeal} pasti</div>
                </div>

                <div className="rv-card">
                  <h3>Per reparto</h3>
                  <div className="rv-chips">
                    {byWard.map(([w, n]) => (
                      <span key={w} className="rv-chip">{w}: {n}</span>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* T-441 — BLOCCO A: trittico per dieta (aggregato) — solo con letti da controllare */}
            {totalMealsForMeal > 0 && (
            <div className="rv-card">
              <h3>Assegna i piatti (trittico)</h3>
              <p className="rv-hint">
                Ogni dieta ha già <strong>primo · secondo · contorno · frutta · bevanda</strong> proposti in automatico
                (bevanda «Acqua» di default). Tocca uno slot per cambiarlo, poi <strong>«Salva trittico»</strong>. Gli slot
                in <span style={{ color: '#b45309', fontWeight: 700 }}>arancione</span> sono diete senza composizione automatica: vanno composte a mano.
              </p>
              {dietGroups.map((g) => (
                <div className="rv-trit" key={g.key}>
                  <div className="rv-trit-h">
                    <span className="rv-diet">{g.dietLabel}</span>
                    <span className="rv-n">{g.meals}</span>
                  </div>
                  {renderSlots(g.key, g.dietCode)}
                  <div className="rv-beds">{g.beds.map((b) => `${b.reparto ? `${b.reparto} ` : ''}letto ${b.letto}`).join(' · ')}</div>
                </div>
              ))}
              {dietGroups.length === 0 && <div className="rv-hint">Nessuna dieta senza note per {MEAL_LABELS[reviewMeal].toLowerCase()}.</div>}
            </div>
            )}

            {/* T-441 — BLOCCO B: righe scorporate (con note), trittico individuale */}
            {scorporate.length > 0 && (
              <div className="rv-card">
                <h3>Diete personalizzate (con note)</h3>
                <p className="rv-hint">Letti con una nota: gestisci il trittico caso per caso.</p>
                {scorporate.map((s) => (
                  <div className="rv-trit scorp" key={s.key}>
                    <div className="rv-trit-h">
                      <span className="rv-diet">{s.reparto ? `${s.reparto} · ` : ''}letto {s.letto}</span>
                      <span className="rv-badge">{s.dietLabel}</span>
                    </div>
                    {s.notes && <div className="rv-scorp-note">📝 {s.notes}</div>}
                    {renderSlots(s.key, s.dietCode)}
                  </div>
                ))}
              </div>
            )}

            {/* T-443 — ORDINE ACCETTATO dalla cucina: sola lettura (come lo vede il reparto).
                Non più modificabile, ma il preposto lo consulta e lo stampa. */}
            {acceptedBedsForMeal.length > 0 && (
              <div className="rv-card rv-accepted">
                <h3>✅ {MEAL_LABELS[reviewMeal]} accettato dalla cucina</h3>
                <p className="rv-hint">Ordine confermato{acceptedStatus && acceptedStatus !== 'accepted' ? ` (${acceptedStatus})` : ''}: sola lettura. Puoi comunque consultarlo e stamparlo.</p>
                {acceptedBedsForMeal
                  .slice()
                  .sort((a, b) => (a.reparto ?? '').localeCompare(b.reparto ?? '') || (parseInt(a.letto ?? '', 10) || 0) - (parseInt(b.letto ?? '', 10) || 0))
                  .map((l, i) => {
                    const dishes = [l.primo, l.secondo, l.contorno, l.frutta, l.bevanda].map((sl) => sl?.label).filter(Boolean).join(' · ');
                    return (
                      <div className="rv-acc" key={`${l.reparto ?? ''}|${l.letto}|${i}`}>
                        <div className="rv-acc-h"><strong>{l.reparto ? `${l.reparto} · ` : ''}letto {l.letto}</strong> — {l.description}</div>
                        {l.notes && <div className="rv-scorp-note">📝 {l.notes}</div>}
                        <div className="rv-acc-t">{dishes || '— trittico non assegnato —'}</div>
                      </div>
                    );
                  })}
              </div>
            )}

            <IonButton expand="block" fill="outline" style={{ margin: '4px 2px' }} onClick={() => printSummary()}>
              🖨️ Stampa foglio riepilogo (A4)
            </IonButton>
            <div style={{ margin: '4px 2px 8px' }}>
              <IonButton fill="clear" size="small" onClick={() => router.push('/preposto/ordini-letto', 'forward')}>
                ✏️ Modifica gli ordini per letto
              </IonButton>
            </div>
          </>
        )}

        {/* T-441 — Picker piatto, filtrato per la sezione dello slot (primi coi primi…) */}
        <IonModal isOpen={picker !== null} onDidDismiss={() => setPicker(null)} initialBreakpoint={0.9} breakpoints={[0, 0.9, 1]}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{picker ? `Scegli ${picker.title.toLowerCase()}` : 'Scegli piatto'}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setPicker(null)}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonSearchbar value={pickerSearch} onIonInput={(e) => setPickerSearch(String(e.detail.value ?? ''))} placeholder="Cerca piatto…" debounce={0} />
            {picker && (() => {
              const q = pickerSearch.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
              const cur = tritById[picker.key]?.[picker.slot]?.label ?? null;
              // T-445 - BEVANDA: nessuna categoria bevande a catalogo -> lista fissa in codice.
              if (picker.slot === 'bevanda') {
                const norm = (s: string): string => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                const opts = BEVANDA_OPTIONS.filter((b) => !q || norm(b).includes(q));
                if (opts.length === 0) return <div className="rv-hint" style={{ padding: 12 }}>Nessuna bevanda corrisponde.</div>;
                return (
                  <div>
                    {opts.map((b) => (
                      <button
                        key={b}
                        type="button"
                        className={`rv-pick${cur === b ? ' on' : ''}`}
                        onClick={() => { setSlot(picker.key, 'bevanda', b); setPicker(null); }}
                      >
                        <span>{b === '-' ? 'nessuna bevanda' : b}</span>
                        {cur === b && <span className="tick">{'\u2713'}</span>}
                      </button>
                    ))}
                  </div>
                );
              }
              const section = SLOT_SECTION[picker.slot];
              const rows = catalog
                .filter((c) => c.category.toUpperCase() === section)
                .filter((c) => !q || c.label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(q));
              if (rows.length === 0) return <div className="rv-hint" style={{ padding: 12 }}>Nessun piatto in questa sezione del catalogo.</div>;
              return (
                <div>
                  {rows.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={`rv-pick${cur === c.label ? ' on' : ''}`}
                      onClick={() => { setSlot(picker.key, picker.slot, c.label); setPicker(null); }}
                    >
                      <span>{c.label}</span>
                      {cur === c.label && <span className="tick">✓</span>}
                    </button>
                  ))}
                  {/* consente di svuotare lo slot */}
                  <button type="button" className="rv-pick clear" onClick={() => { setTritById((m) => ({ ...m, [picker.key]: { ...(m[picker.key] ?? EMPTY_TRIT), [picker.slot]: null } })); setPicker(null); }}>
                    ✕ Nessuno (lascia vuoto)
                  </button>
                </div>
              );
            })()}
            <div style={{ height: 24 }} />
          </IonContent>
        </IonModal>

        {/* T-436 fix — foglio riepilogo IN-APP (iframe), sempre chiudibile */}
        <IonModal isOpen={printHtml !== null} onDidDismiss={() => setPrintHtml(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Foglio riepilogo</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setPrintHtml(null)}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <iframe
              ref={printRef}
              srcDoc={printHtml ?? ''}
              title="Foglio riepilogo"
              style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
            />
          </IonContent>
          <IonFooter>
            <IonToolbar>
              <div style={{ padding: '6px 12px' }}>
                <IonButton expand="block" onClick={() => doPrint()}>🖨️ Stampa</IonButton>
              </div>
            </IonToolbar>
          </IonFooter>
        </IonModal>

        <IonToast isOpen={toast !== null} message={toast ?? ''} duration={2600} onDidDismiss={() => setToast(null)} />
      </IonContent>
      {!loading && totalMealsForMeal > 0 && (
        <IonFooter>
          <IonToolbar>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px' }}>
              <IonButton fill="outline" style={{ flex: 1 }} disabled={savingTrit} onClick={() => void saveTrittico()}>
                {savingTrit ? 'Salvo…' : '💾 Salva trittico'}
              </IonButton>
              {draftMealsForMeal > 0 && (
                <IonButton style={{ flex: 1.3 }} disabled={sending} onClick={() => setConfirmSend(true)}>
                  {sending ? 'Invio…' : `Verifica e invia ${MEAL_LABELS[reviewMeal].toLowerCase()} (${draftMealsForMeal})`}
                </IonButton>
              )}
            </div>
          </IonToolbar>
        </IonFooter>
      )}

      {/* Conferma prima dell'invio al centro cottura: dopo l'invio l'ordine NON è più
          modificabile (né dal reparto né dal preposto), quindi lo avvisiamo. */}
      <IonAlert
        isOpen={confirmSend}
        onDidDismiss={() => setConfirmSend(false)}
        header={`Invia ${MEAL_LABELS[reviewMeal].toLowerCase()} al centro cottura?`}
        message={`Stai inviando ${draftMealsForMeal} ${draftMealsForMeal === 1 ? 'letto' : 'letti'}. Dopo l'invio l'ordine del ${MEAL_LABELS[reviewMeal].toLowerCase()} va in produzione e NON potrà più essere modificato. Verifica che sia tutto corretto.`}
        buttons={[
          { text: 'Annulla', role: 'cancel' },
          { text: 'Invia definitivamente', role: 'confirm', handler: () => { void sendToKitchen(); } },
        ]}
      />
    </IonPage>
  );
}
